'use client';

import { ComponentPropsWithRef, forwardRef } from 'react';

import { Slottable } from '@radix-ui/react-slot';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type TooltipIconButtonProps = ComponentPropsWithRef<typeof Button> & {
  tooltip: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Force the tooltip to stay open (useful for showing persistent status) */
  forceTooltipOpen?: boolean;
};

export const TooltipIconButton = forwardRef<HTMLButtonElement, TooltipIconButtonProps>(
  (
    {
      children,
      tooltip,
      side = 'bottom',
      className,
      forceTooltipOpen,
      disabled,
      onClick,
      type,
      ...rest
    },
    ref,
  ) => {
    // Disabled is aria-only so the button stays hoverable and its tooltip can
    // explain why it is unavailable — which also leaves it activatable. Swallow
    // the event rather than dropping the handler: an unguarded click still runs
    // the default action (submitting an enclosing form) and still bubbles.
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      onClick?.(event);
    };

    // `key` forces a remount when toggling the forced state — Radix Root
    // warns when `open` switches between controlled (`true`) and uncontrolled
    // (`undefined`) on the same instance.
    return (
      <Tooltip key={forceTooltipOpen ? 'forced' : 'auto'} open={forceTooltipOpen || undefined}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            {...rest}
            type={disabled ? 'button' : type}
            aria-disabled={disabled}
            onClick={handleClick}
            className={cn(
              'aui-button-icon size-6 p-1',
              disabled && 'cursor-not-allowed opacity-50',
              className,
            )}
            ref={ref}
          >
            <Slottable>{children}</Slottable>
            <span className="aui-sr-only sr-only">{tooltip}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side={side}>{tooltip}</TooltipContent>
      </Tooltip>
    );
  },
);

TooltipIconButton.displayName = 'TooltipIconButton';
